import { useAuthContext } from "../../auth/state/AuthContext";
import { deleteDocument } from "../../documents/services/documents.service";
import { CaretDownIcon, DownloadAndSendIcon, DownloadIcon, PlusIcon, TrashIcon } from "../../shared/Icons";
import { ReactComponent as CaretRight } from "../../shared/assets/caretRight.svg";
import { docAppendicesAtom, downloadProgressAtom, themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import WithTooltip from "../../shared/components/WithTooltip";
import { postData } from "../../shared/services/service";
import {
  cn,
  downloadFile,
  formatDate,
  hasExpired,
  hasRecentlyExpired,
  isExpiringSoon,
  openDocumentInNewTab,
} from "../../shared/services/utils";
import { Certificate } from "../models/certificates.interfaces";
import { AppendicesExpanded } from "./AppendicesExpanded";
import DeleteConfirmation from "./DeleteConfirmation";
import UploadButton from "./UploadButton";
import { datadogRum } from "@datadog/browser-rum";
import { PrimitiveAtom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

type CertificateWithChecked = {
  certificate: Certificate;
  isChecked: boolean;
};

type CertificatesTableRowProps = {
  certificateAtom: PrimitiveAtom<CertificateWithChecked>;
  index: number;
  openModal: (certificates: Certificate[]) => void;
  searchDocs: () => void;
};

export function toPascalCase(str: string): string {
  return str
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join("");
}

export const CertificatesTableRow = ({ certificateAtom, index, openModal, searchDocs }: CertificatesTableRowProps) => {
  const theme = useAtomValue(themeAtom);
  const [certificateState, setCertificateState] = useAtom(certificateAtom);
  const { nextSurveyDate, expiresOn } = certificateState.certificate;
  const certificateHasExpired = hasExpired(expiresOn);
  const certificateWillExpire = !certificateHasExpired && isExpiringSoon(expiresOn, 90);
  const surveyWillExpire = nextSurveyDate && isExpiringSoon(certificateState.certificate.nextSurveyDate, 90);
  const surveyHasExpired = nextSurveyDate && hasExpired(nextSurveyDate);
  const surveyExpiredWithinTolerance = surveyHasExpired && hasRecentlyExpired(nextSurveyDate, 90);
  const cert = certificateState.certificate;
  // 20 out of 7400 Q88 docs have failed to transfer.
  // We label them in the FE and filter them out when downloading the zip (prevents BE crash).
  const documentMissingInS3 = certificateState.certificate.s3Path?.includes("ERROR_LOADING");
  const { sub: email } = useAuthContext().state.user?.accessToken || {};

  const [isExpanded, setIsExpanded] = useState(false);

  const setDownloadProgress = useSetAtom(downloadProgressAtom);

  const handleCheckboxClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setCertificateState((prevState) => ({
      ...prevState,
      isChecked: !prevState.isChecked,
    }));
  };

  const sendFileByEmail = async (certificate: Certificate): Promise<void> => {
    openModal([certificate]);
    datadogRum.addAction("sendDocumentByEmail", {
      ext_user_email: email,
    });
  };

  // We no longer display the modal
  // Emails are no longer sent via frontend, they are sent via backedn email sarvice v2.
  const triggerDownloadFile = async (certificate: Certificate): Promise<void> => {
    // openModal(); // DEPRECATED We now have dedicated send email with attachment method in backend
    datadogRum.addAction("downloadDocument", {
      ext_user_email: email,
    });

    const fileName =
      `${toPascalCase(certificate.docName)}_${toPascalCase(certificate.vesselName)}_${certificate.imoNumber}`
        .replace(/\//g, "-")
        .trim();

    try {
      const {
        data: { url },
      } = await postData(`documents/presigned-url`, { documentKey: certificate.s3Path });

      await downloadFile(
        {
          url,
          fileName,
        },
        setDownloadProgress,
      );
    } catch (err) {
      console.log("handleClickDownload error", err);
    }
  };

  const [docAppendices] = useAtom(docAppendicesAtom);
  const appendices = docAppendices.filter((appendix) => appendix.appendixFor === cert.s3Path);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirmDeleteYes = async () => {
    setShowDeleteConfirmation(false);
    setIsLoading(true);

    // Deactivates the appendixes if there are any and also deactivates the Parent Document
    await Promise.all(appendices.map((appendix) => deleteDocument(appendix.s3Path ?? "")));
    await deleteDocument(cert.s3Path ?? "");

    setIsLoading(false);
    searchDocs();
  };

  const handleConfirmDeleteNo = () => {
    setShowDeleteConfirmation(false);
  };

  function singularize(docType: string): string {
    if (docType.endsWith("s")) {
      return docType.slice(0, -1);
    }
    return docType;
  }

  const bqVesselSubmission =
    cert.docName === "Baltic questionnaire" && (cert.source === "Vessel submission" || cert.source === "Vessel update");
  const bqManualUpload = cert.docName === "Baltic questionnaire" && cert.source === "Manual upload";

  return (
    <>
      <tr
        className={cn(
          "group h-14 cursor-pointer border-b-[1px] text-left text-xs",
          theme.myVesselsTableRow.row.hover,
          theme.myVesselsTable.tailwind.tableBorder,
          theme.uwhcAssistant.tableText,

          {
            "border-l-2 border-l-green": isExpanded,
          },
        )}
        key={index}
        onClick={() => {
          if (appendices?.length) setIsExpanded(!isExpanded);
        }}
      >
        <td className="px-4 text-sm">
          <div className={twMerge("justify-left align-center flex flex-row", theme.myVesselsTableRow.row.text)}>
            <div className="flex flex-col justify-center pr-2">
              <input
                type="checkbox"
                checked={certificateState.isChecked}
                onClick={handleCheckboxClick}
                className={twMerge("form-checkbox h-4 w-4 rounded")}
                onChange={() => {
                  // This empty handler keeps the console clean for now.
                }}
              />
            </div>
            {!!appendices?.length && (
              <div className="pr-[8px] pt-[3px]">{isExpanded ? <CaretDownIcon /> : <CaretRight />}</div>
            )}
            {/* Here is the cert name */}
            <div onClick={() => openDocumentInNewTab(cert)}>
              {cert.docName}
              {cert.portOfIssueIsWhoApproved === true && (
                <span
                  className={twMerge(
                    "ml-2 whitespace-nowrap rounded-md p-1 text-[10px]",
                    theme.uwhcAssistant.sendBg,
                    theme.uwhcAssistant.sendText,
                  )}
                >
                  Port is WHO approved
                </span>
              )}
            </div>
          </div>
        </td>

        <td className="px-4">{cert.docType}</td>
        <td className="px-4">{cert.source}</td>
        <td
          className={`flex w-[100px] flex-row items-center justify-between px-4 pt-[16px] ${(bqVesselSubmission || bqManualUpload) && "invisible"}`}
        >
          <div className="pt-[3px]">{appendices?.length ?? 0}</div>
          <WithTooltip
            className={twMerge("opacity-0 group-hover:opacity-100")}
            id="add-an-appendix"
            content="Add an appendix"
            place="top"
            tooltipClasses="max-w-[180px] z-[100]"
          >
            <UploadButton
              parentDoc={cert}
              searchDocs={searchDocs}
              inner={
                <PlusIcon
                  className={twMerge(
                    "text-red h-5 w-5 rounded-full border",
                    theme.crtDropInput.borderGreen,
                    theme.home.tailwind.spinner,
                  )}
                />
              }
            />
          </WithTooltip>
        </td>

        <td className={`px-4 ${cert.expiresOn && (certificateWillExpire || certificateHasExpired) && "text-red-600"} `}>
          {cert.expiresOn ? formatDate(cert.expiresOn) : ""}
        </td>

        <td className={`px-4 ${cert.nextSurveyDate && (surveyWillExpire || surveyHasExpired) && "text-red-600"}`}>
          {cert.nextSurveyDate ? formatDate(cert.nextSurveyDate) : ""}
        </td>

        <td className="px-4">
          {certificateHasExpired && <Warning text="Document expired" />}
          {documentMissingInS3 && <Warning text="Missing, please reupload" isWarning />}
          {!certificateHasExpired && surveyExpiredWithinTolerance && (
            <Warning text="Endorsement overdue (within tolerance period)" isWarning />
          )}
          {!certificateHasExpired && surveyHasExpired && !surveyExpiredWithinTolerance && (
            <Warning text="Endorsement overdue" />
          )}
          {!certificateHasExpired && !surveyHasExpired && certificateWillExpire && (
            <Warning text="Document about to expire" isWarning />
          )}
        </td>

        {/* Actions (Download, Resend..) */}
        <td className="flex flex-row items-center">
          <div
            className={twMerge(
              "flex cursor-pointer gap-2 pr-4 pt-4 text-sm opacity-0 group-hover:opacity-100",
              theme.crtDropInput.textGreen,
            )}

            onClick={() => triggerDownloadFile(cert)}
          >
            <DownloadIcon />
            <div className="pt-1">Download</div>
          </div>
          <div
            className="flex cursor-pointer gap-2 pt-4 text-[#00a194] opacity-0 group-hover:opacity-100"
            onClick={() => sendFileByEmail(cert)}
          >
            <DownloadAndSendIcon className={theme.crtDropInput.textGreen} />
            <WithTooltip
              id="send-email"
              content="Send file by email. Takes a few seconds."
              place="top"
              tooltipClasses="max-w-[180px] z-[100]"
            >
              Share by email
            </WithTooltip>
          </div>
          <WithTooltip
            className={`mt-4 opacity-0 group-hover:opacity-100 ${(bqVesselSubmission || bqManualUpload) && "invisible"}`}
            id="add-an-appendix"
            content={`Delete ${singularize(cert.docType)}`}
            place="top"
            tooltipClasses="max-w-[180px] z-[100]"
          >
            <TrashIcon
              className="ml-4 size-4 cursor-pointer opacity-0 group-hover:opacity-100"
              color={theme.certDocumentIcon.trash}
              onClick={() => {
                setShowDeleteConfirmation(true);
              }}
            />
            {showDeleteConfirmation && (
              <DeleteConfirmation
                message={`Are you sure you want to delete ${cert.docName}`}
                onClickYes={handleConfirmDeleteYes}
                onClickNo={handleConfirmDeleteNo}
              />
            )}
          </WithTooltip>
        </td>
      </tr>

      {isExpanded && <AppendicesExpanded document={cert} appendices={appendices} searchDocs={searchDocs} />}

      {isLoading && (
        <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg" />
      )}
    </>
  );
};

export default CertificatesTableRow;

type WarningProps = {
  text: string;
  isWarning?: boolean;
  className?: string;
};

export function Warning({ text, isWarning, className }: WarningProps) {
  const theme = useAtomValue(themeAtom);

  return (
    <div className={`flex flex-row pt-1 ${className ?? ""}`}>
      <div
        className={[
          "mr-2 flex h-[20px] w-[20px] flex-row justify-center rounded-full",
          isWarning ? theme.warningFunction.yellow : theme.warningFunction.red,
          theme.warningFunction.text,
        ].join(" ")}
      >
        <div className="h-[25px] pt-[2px] font-bold">!</div>
      </div>
      <div className={["flex flex-1 flex-row items-center", theme.warningFunction.messageText].join(" ")}>{text}</div>
    </div>
  );
}
